'use client';
import { useEffect, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { TagBlock } from '@/app/components/shared/ElementDialog/components/Tag';
import type { TagType } from '@/app/types';
import { cn } from '@/app/utils';

interface TagSelectProps {
  tags: TagType[];
  selectedTags: string[];
  onTagSelect: (value: string, name?: string) => void;
  className?: string;
  size?: 'small' | 'default';
  onDelete?: (id: string) => void;
}

export function LabelFilter({
  tags,
  selectedTags,
  onTagSelect,
  className,
  size = 'default',
  onDelete,
}: TagSelectProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const shouldShowLeftArrow = container.scrollLeft > 0;

    const hasMoreToScroll = container.scrollWidth - container.clientWidth - container.scrollLeft > 8;
    const shouldShowRightArrow = hasMoreToScroll;

    if (shouldShowLeftArrow !== showLeftArrow || shouldShowRightArrow !== showRightArrow) {
      setIsAnimating(true);

      setTimeout(() => {
        setIsAnimating(false);
      }, 150);
    }

    setShowLeftArrow(shouldShowLeftArrow);
    setShowRightArrow(shouldShowRightArrow);
  };

  useEffect(() => {
    if (tags.length) {
      checkScrollButtons();
    }
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [tags]);

  // 滚动时检查
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isAnimating) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        checkScrollButtons();
      }, 50);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isAnimating]);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className={cn('flex items-center', className)}>
      <div
        onClick={scrollLeft}
        className={cn(
          'mt-1 flex flex-shrink-0 cursor-pointer items-center justify-center rounded-6 bg-system-white10 text-white transition-[width] hover:bg-system-white24 sm:hidden',
          size === 'small' ? 'size-7' : 'size-8',
          showLeftArrow ? (size === 'small' ? 'w-7' : 'w-8') : 'w-0',
          showLeftArrow && 'mr-2',
        )}
        aria-label="Scroll left"
        onTransitionEnd={() => setIsAnimating(false)}
      >
        <BaseLineLeft className="size-4 text-system-white64" />
      </div>

      <div
        ref={scrollContainerRef}
        className={cn('hide-scrollbar flex flex-1 gap-2 overflow-x-auto scroll-smooth pt-1 pr-2')}
        onScroll={() => {
          if (!isAnimating) {
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
              checkScrollButtons();
            }, 50);
          }
        }}
      >
        {tags?.map((tag) => {
          const isSelected = selectedTags?.includes(tag.id || '');
          return (
            <TagBlock
              key={tag.id}
              tag={tag}
              isSelected={isSelected}
              onTagSelect={onTagSelect}
              onDelete={onDelete}
              enableDelete={!!onDelete}
              innerClassName={size === 'small' ? 'text-xs' : 'text-sm'}
              wrapperClassName="flex-shrink-0"
              size={size}
              bgConfig={{
                unSelect: 'bg-system-white10',
                hover: 'hover:bg-system-white24',
              }}
            />
          );
        })}
      </div>

      <div
        onClick={scrollRight}
        className={cn(
          'mt-1 flex flex-shrink-0 cursor-pointer items-center justify-center rounded-6 bg-system-white10 text-white transition-[width] hover:bg-system-white24 sm:hidden',
          size === 'small' ? 'size-7' : 'size-8',
          showRightArrow ? (size === 'small' ? 'w-7' : 'w-8') : 'w-0',
          showRightArrow && 'ml-2',
        )}
        aria-label="Scroll right"
        onTransitionEnd={() => setIsAnimating(false)}
      >
        <BaseLineRight className="size-4 text-system-white64" />
      </div>
    </div>
  );
}
